import { inject, Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import { PlayerRequest, PlayerSummary } from '@core/models/player.models'
import { Observable, of } from 'rxjs'
import { filter, map, switchMap, take, tap } from 'rxjs/operators'
import { setPlayerALoader } from '@pages/player-detail/store/actions/player-detail.actions'
import { concatLatestFrom } from '@ngrx/operators'
import {
  selectAggregationsLeftAggregation,
  selectAggregationsRightAggregation
} from '@features/aggregations/store/selectors/aggregations.selectors'
import { requestErrorHandlerWithCallback } from '@shared/operators/request-error.operator'
import { PlayerApi } from '@core/requests/api/player/player.api'
import { Match } from '@core/models/match.models'
import { MetricMap } from '@core/models/metric.models'
import { selectMatch } from '@pages/match-detail/store/selectors/match-detail.selectors'
import { fetchMatch } from '@pages/match-detail/store/actions/match-detail.actions'
import { completeWhen } from '@mediacoach/ui'

@Injectable({ providedIn: 'root' })
export class MatchPlayerNgrxService {
  private readonly _store: Store = inject(Store)
  private readonly _api: PlayerApi = inject(PlayerApi)

  fetchMatchIfMissing(matchId: string) {
    this._store
      .select(selectMatch)
      .pipe(
        take(1),
        filter((match) => !match)
      )
      .subscribe(() => {
        this._store.dispatch(fetchMatch(matchId))
      })
  }

  resolveMatchPlayerSummary(
    payload: PlayerRequest,
    callbackOnError: () => void
  ): Observable<{ metrics: MetricMap; summary: PlayerSummary; match: Match }> {
    return of(payload).pipe(
      tap(() => this._store.dispatch(setPlayerALoader({ playerALoading: true }))),

      switchMap((payload) =>
        this._store.select(selectMatch).pipe(
          completeWhen((match) => !!match),
          map((match) => ({ payload, match }))
        )
      ),
      concatLatestFrom(() => [
        this._store.select(selectAggregationsLeftAggregation),
        this._store.select(selectAggregationsRightAggregation)
      ]),
      map(([{ match, payload }, leftAggregationDataSource, rightAggregationDataSource]) => ({
        payload: {
          ...payload,
          leftAggregationDataSource,
          rightAggregationDataSource
        },
        match
      })),
      switchMap(({ payload, match }) =>
        this._api
          .fetchMatchPlayer(
            match.seasonId,
            match.competitionId,
            match.id,
            payload.playerId,
            payload.teamId,
            payload.leftAggregationDataSource.id,
            payload.rightAggregationDataSource.id,
            payload.playerPosition
          )
          .pipe(
            map(({ metricCategories, metricsOverview, chartMetricResults, ...summary }) => {
              return {
                summary: {
                  ...summary,
                  metricPlayerPosition: payload.playerPosition,
                  teamId: payload.teamId,
                  matchId: match.id,
                  seasonId: match.seasonId,
                  competitionId: match.competitionId
                } as PlayerSummary,
                metrics: { metricCategories, metricsOverview, chartMetricResults },
                match
              }
            }),
            requestErrorHandlerWithCallback(callbackOnError)
          )
      )
    )
  }
}
